import React, { useEffect, useState } from 'react';
import {
  Page,
  Content,
  Header,
  Progress,
  Table,
  TableColumn,
} from '@backstage/core-components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { platformAuthApiRef } from '../../apis';

type ApiKey = {
  id: string;
  name: string;
  //createdAt: string;
  //lastUsedAt?: string;
  value: string; // the actual key
};

export const ApiKeysPage = () => {
  const config = useApi(configApiRef);
  const platformAuth = useApi(platformAuthApiRef);
  const [loading, setLoading] = useState(false);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  const handleCopyKey = async (keyId: string, keyValue: string) => {
    try {
      await navigator.clipboard.writeText(keyValue);
      setCopied(prev => ({ ...prev, [keyId]: true }));

      // Reset the "copied" state after 2 seconds
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [keyId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy key:', err);
    }
  };

  const columns: TableColumn<ApiKey>[] = [
    { title: 'Name', field: 'name' },
    {
      title: 'Key',
      field: 'value',
      render: rowData => {
        const isRevealed = !!revealed[rowData.id];
        const displayValue = isRevealed
          ? rowData.value
          : '••••••••••••••••••••';

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <code
              style={{
                padding: '2px 6px',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.04)',
                fontSize: '0.8rem',
              }}
            >
              {displayValue}
            </code>
            <Tooltip title={isRevealed ? 'Hide key' : 'Reveal key'}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setRevealed(prev => ({
                    ...prev,
                    [rowData.id]: !isRevealed,
                  }));
                }}
              >
                {isRevealed ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={copied[rowData.id] ? 'Copied!' : 'Copy key'}>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleCopyKey(rowData.id, rowData.value);
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    //{ title: 'Created', field: 'createdAt' },
    //{ title: 'Last Used', field: 'lastUsedAt' },
  ];

  const loadKeys = async () => {
    setLoading(true);
    setError(undefined);

    try {
      const backendBaseUrl = config.getString('backend.baseUrl');

      // Get the OIDC access token from platformAuth
      const accessToken = await platformAuth.getAccessToken(['openid', 'profile', 'email']);

      if (!accessToken) {
        throw new Error('Not authenticated - please log in');
      }

      const response = await fetch(`${backendBaseUrl}/api/mock-api-keys/api-keys`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Not authenticated - please log in');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load keys: ${response.status}`);
      }

      const data: ApiKey[] = await response.json();
      setKeys(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    setCreating(true);
    setError(undefined);

    try {
      // generate a fake-looking key
      const randomPart = Math.random().toString(36).slice(2, 10).toUpperCase();
      const newKey: ApiKey = {
        id: `key-${Date.now()}`,
        name: `Key ${new Date().toISOString()}`,
        //createdAt: new Date().toISOString(),
        //lastUsedAt: undefined,
        value: `sk_live_${randomPart}`,
      };

      setKeys(prev => [newKey, ...prev]);

      // new key starts hidden
      setRevealed(prev => ({ ...prev, [newKey.id]: false }));
    } catch (e: any) {
      setError('Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  return (
    <Page themeId="tool">
      <Header
        title="API Keys"
        // subtitle="Manage client credentials for your APIs"
      >
        {/* <Button
          variant="contained"
          disabled={creating}
          onClick={createKey}
        >
          {creating ? 'Creating…' : 'Create API Key'}
        </Button>*/}
      </Header> 
      <Content>
        {loading && <Progress />}
        {error && (
          <div style={{ color: 'salmon', marginBottom: 16 }}>
            {error}
          </div>
        )}
        {!loading && (
          <Table
            title="Your API Keys"
            options={{
              paging: false,
              search: false,
              toolbar: false,
            }}
            columns={columns}
            data={keys}
          />
        )}
      </Content>
    </Page>
  );
};

export default ApiKeysPage;



// import React, { useEffect, useState } from 'react';
// import {
//   Page,
//   Content,
//   Header,
//   Progress,
//   Table,
//   TableColumn,
// } from '@backstage/core-components';
// import { useApi, configApiRef } from '@backstage/core-plugin-api';
// import Button from '@mui/material/Button';

// type ApiKey = {
//   id: string;
//   name: string;
//   key: string;
//   //createdAt: string;
//   //lastUsedAt?: string;
// };

// export const ApiKeysPage = () => {
//   const configApi = useApi(configApiRef);
//   const backendBaseUrl = configApi.getString('backend.baseUrl'); // e.g. http://localhost:7007

//   const [loading, setLoading] = useState(false);
//   const [keys, setKeys] = useState<ApiKey[]>([]);
//   const [creating, setCreating] = useState(false);
//   const [error, setError] = useState<string | undefined>();

//   const columns: TableColumn<ApiKey>[] = [
//     { title: 'Name', field: 'name' },
//     { title: 'Key', field: 'key' }
//   ];

//   const loadKeys = async () => {
//     setLoading(true);
//     setError(undefined);
//     try {
//       const url = `${backendBaseUrl}/api/platform-cloud/authn/api-keys`;
//       console.log('CALLING BACKEND (direct):', url);

//       const res = await fetch(url, {
//         // include credentials if you need cookies:
//         // credentials: 'include',
//       });

//       const contentType = res.headers.get('content-type') ?? '';
//       const text = await res.text();

//       if (!res.ok) {
//         console.error('Failed to load keys:', res.status, text);
//         throw new Error(`Failed to load keys: ${res.status}`);
//       }

//       if (!contentType.includes('application/json')) {
//         console.error(
//           'Expected JSON when loading keys, got:',
//           contentType,
//           'body:',
//           text,
//         );
//         throw new Error('API did not return JSON');
//       }

//       const data = JSON.parse(text);
//       setKeys(data);
//     } catch (e: any) {
//       setError(e.message ?? 'Failed to load keys');
//     } finally {
//       setLoading(false);
//     }
//   };

// const loadKeys = async () => {
//   setLoading(true);
//   setError(undefined);

//   // 🔧 TEMP: Hardcoded API keys
//   const fakeKeys: ApiKey[] = [
//     {
//       id: 'key-1',
//       name: 'Restricted',
//       key: 'Ztg3GV6v1h5eS0QZLzRq9aui7CsaRdzI7xpvbL7u'
//     },
//     // {
//     //   id: 'key-2',
//     //   name: 'Unestricted',
//     //   key: 'hyYe5BWeNz53S8dd6XN4x1EjjVnjWaLi3VjVqyYe'
//     // },

//   ];

//   // Simulate small load delay
//   await new Promise(res => setTimeout(res, 300));

//   setKeys(fakeKeys);
//   setLoading(false);
// };

//   const createKey = async () => {
//     setCreating(true);
//     setError(undefined);
//     try {
//       const url = `${backendBaseUrl}/api/platform-cloud/authn/api-keys`;
//       console.log('CALLING BACKEND (direct, POST):', url);

//       const res = await fetch(url, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         // include credentials if needed:
//         // credentials: 'include',
//         body: JSON.stringify({
//           name: `Key ${new Date().toISOString()}`,
//         }),
//       });

//       const contentType = res.headers.get('content-type') ?? '';
//       const text = await res.text();

//       if (!res.ok) {
//         console.error('Failed to create key:', res.status, text);
//         throw new Error(`Failed to create key: ${res.status}`);
//       }

//       if (!contentType.includes('application/json')) {
//         console.error(
//           'Expected JSON when creating key, got:',
//           contentType,
//           'body:',
//           text,
//         );
//         // you can choose to ignore this if the POST returns no body
//       }

//       await loadKeys();
//     } catch (e: any) {
//       setError(e.message ?? 'Failed to create key');
//     } finally {
//       setCreating(false);
//     }
//   };

//   useEffect(() => {
//     loadKeys();
//   }, []);

//   return (
//     <Page themeId="tool">
//       <Header
//         title="API Keys"
//         subtitle="Manage client credentials for your APIs"
//       >
//         <Button variant="contained" disabled={creating} onClick={createKey}>
//           {creating ? 'Creating…' : 'Create API Key'}
//         </Button>
//       </Header>
//       <Content>
//         {loading && <Progress />}
//         {error && (
//           <div style={{ color: 'red', marginBottom: 16 }}>
//             {error}
//           </div>
//         )}
//         {!loading && (
//           <Table
//             title="Your API Keys"
//             options={{ paging: false, search: false }}
//             columns={columns}
//             data={keys}
//           />
//         )}
//       </Content>
//     </Page>
//   );
// };

// export default ApiKeysPage;
